import type { ReactNode } from 'react'
import React, { useContext, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

export const EVENTS = {
  onLocationChange: 'webview:on-location-change',
  onAuthSuccess: 'webview:on-auth-success',
  openCreatePromotionModal: 'webview:open-create-promotion-modal',
  openCreateProductModal: 'webview:open-create-product-modal',
} as const

type Context = {
  events: typeof EVENTS | null
  dispatchEvent: (event: keyof typeof EVENTS, payload?: any) => void
  addEventListener: (event: keyof typeof EVENTS, handler: (payload?: any) => void) => () => void
}

type WebviewEventsBusProviderProps = {
  children: ReactNode
}

export const WebviewEventsBusContext = React.createContext<Context | undefined>(undefined)

export const WebviewEventsBusProvider = ({ children }: WebviewEventsBusProviderProps) => {
  const location = useLocation()

  const dispatchEvent = (event: keyof typeof EVENTS, payload?: any) => {
    document.dispatchEvent(new CustomEvent(EVENTS[event], { detail: payload }))
  }

  const addEventListener = (event: keyof typeof EVENTS, handler: (payload?: any) => void) => {
    const eventHandler = (e: CustomEvent) => handler(e.detail)
    document.addEventListener(EVENTS[event], eventHandler as EventListener)

    return () => {
      document.removeEventListener(EVENTS[event], eventHandler as EventListener)
    }
  }

  useEffect(() => {
    dispatchEvent('onLocationChange', location)
  }, [location])

  const value = useMemo(
    () => ({
      events: EVENTS,
      dispatchEvent,
      addEventListener,
    }),
    [],
  )

  return (
    <WebviewEventsBusContext.Provider value={value}>{children}</WebviewEventsBusContext.Provider>
  )
}

export function useWebviewEventsBus() {
  const context = useContext(WebviewEventsBusContext)

  if (context === undefined) {
    throw new Error('useWebviewEventsBus must be used within a WebviewEventsBusProvider')
  }

  return context
}
